/**
 * English: the source language.
 *
 * Every user-visible string in the dashboard is here, and this file is where a
 * copy edit happens. `typeof en` is the type every other catalogue is checked
 * against, so a key added here without a translation is a compile error in
 * `nl.ts` on the next build — not a Dutch operator finding an English sentence
 * in the middle of a screen.
 *
 * ---------------------------------------------------------------------------
 * KEYS
 * ---------------------------------------------------------------------------
 *
 * Structural, grouped by the screen that shows them: `matrix.save`,
 * `versions.cloneIntoNextSeason`, `quote.noAvailability`. Never keyed by the
 * English text itself — that turns every wording change into a rename across
 * every catalogue, which is how catalogues drift.
 *
 * `{name}` interpolates. A `{ one, other }` value is a plural, chosen on the
 * `count` value passed with it. Emphasis inside a sentence is marked `*so*` and
 * rendered by `<Rich>`, so a translator can move it to the word that carries it
 * in their language.
 *
 * What is NOT here: period labels, accommodation names, rate and version
 * labels, and the messages the API writes. Those are data, and data is shown
 * exactly as the operator typed it.
 */

export interface Plural {
  one: string;
  other: string;
}

export const en = {
  /* ---------------------------------------------------------------- shell */
  'app.language': 'Language',
  'app.season': 'Season',
  'app.dismiss': 'Dismiss',
  'app.acknowledge': 'OK',
  'app.tabPrices': 'Prices',
  'app.tabPeriods': 'Periods',
  'app.tabAccommodation': 'Accommodation',
  'app.tabUnits': 'Units & blocks',
  'app.tabInquiries': 'Enquiries',

  /* --------------------------------------------------------- prices screen */
  'app.pricesHeading': 'Nightly prices · {year}',
  'app.accommodationCount': {
    one: '{count} accommodation',
    other: '{count} accommodations',
  } as Plural,
  'app.periodCount': {
    one: '{count} period',
    other: '{count} periods',
  } as Plural,
  'app.savedAccommodations': {
    one: 'Saved {count} accommodation',
    other: 'Saved {count} accommodations',
  } as Plural,

  /* --------------------------------------------------- accommodation screen */
  'app.accommodationHeading': 'Accommodation',
  'app.addAccommodation': 'Add',
  'app.newAccommodationLabel': 'New accommodation {number}',
  'app.accommodationCreated': 'Accommodation created with an empty draft',
  'app.chooseAccommodation': 'Choose…',
  'app.versionCount': {
    one: '{count} version',
    other: '{count} versions',
  } as Plural,
  'app.liveVersion': '{label} live',
  'app.nothingLive': 'nothing live',
  'app.draftVersion': '{label} draft',
  'app.clonedInto': 'Cloned into {label}',
  /* Three sentences, joined only when they are all true.
     The old single sentence — "this is now the version guests are quoted from"
     — was false the moment a second season went live, which the demo park
     already is. Which version a guest is quoted from depends on when they
     arrive, so the notice says what became live and what else still is. */
  'app.published': 'Published. Guests arriving in the dates it covers are quoted from it.',
  'app.publishedForDates': 'It prices arrivals from {from} up to (but not including) {until}.',
  'app.publishedReplaced': 'It replaced {label}, which covered dates this one also covers.',
  'app.publishedAlsoLive': {
    one: 'It is the only version live for this accommodation.',
    other: '{count} versions are now live for this accommodation, each for its own arrival dates.',
  } as Plural,
  'app.renamed': 'Renamed',
  'app.archived': 'Archived',
  'app.deleted': 'Deleted',
  'app.deleteRate': 'Delete this rule',
  'app.newRuleName': 'New rule',
  'app.deleteRateConfirm': 'Delete \u201c{label}\u201d? Nothing is written until you press Save, and Revert puts it back.',
  'app.versionCreated': 'Created an empty draft. Nothing is priced until it has rates.',
  'app.accommodationSaved': 'Accommodation saved',
  'app.periodsSeeded': 'Added the computed Dutch holidays',
  'app.periodSaved': 'Period saved',
  'app.periodDeleted': 'Period deleted',
  'app.unitSaved': 'Unit saved',
  'app.unitDeleted': 'Unit deleted',
  'app.unitsGenerated': {
    one: 'Added 1 unit',
    other: 'Added {count} units',
  } as Plural,
  'app.terrainSaved': 'Terrain saved',
  'app.blockSaved': 'Blocked {unit}, {from} to {until}',
  'app.blockRemoved': 'Block removed. Those nights are for sale again.',
  'app.inquiryStatusSet': 'Enquiry marked “{status}”',

  'app.subQuote': 'Quote & test',
  'app.subLadder': 'Ladder',
  'app.subGuests': 'Guests & extras',
  'app.subProgramme': 'Programme',
  'app.subPublished': 'Published rates',

  'app.arrival': 'Arrival',
  'app.nights': 'Nights',
  'app.extrasHeading': 'On top of the accommodation',
  'app.buildHeading': 'How the price is built',
  'app.tryDatesHeading': 'Try other dates',
  'app.appliedChanges': {
    one: 'Applied {count} change',
    other: 'Applied {count} changes',
  } as Plural,

  'app.driftHeading': 'Periods that have moved',
  'app.driftNote': 'These rates were written from a library period whose dates have since changed. Nothing has been altered.',
  'app.driftRate': 'Rate',
  'app.driftPeriod': 'Period',
  'app.driftOnRate': 'On the rate',
  'app.driftInLibrary': 'In the library',

  'app.ratesHeading': 'Rates',
  'app.checksHeading': 'Configuration checks',
  'app.checksToReview': '{count} to review',
  'app.checksAllClear': 'all clear',
  'app.programmeSourceHeading': 'Programme source',
  'app.publishedIsReadOnly': 'This version is live and cannot be edited. Clone it first.',
  'app.programmeSaved': 'Programme saved',
  'app.saving': 'Saving…',
  'app.requestFailed': '{method} {path} failed',

  /* ------------------------------------------------------------- shortcuts */
  'keys.title': 'Keyboard shortcuts',
  'keys.close': 'Close',
  'keys.groupGeneral': 'General',
  'keys.groupNavigate': 'Navigate',
  'keys.groupGrid': 'Grid',
  'keys.groupVersions': 'Versions',
  'keys.groupLadder': 'Ladder',
  'keys.showShortcuts': 'Show keyboard shortcuts',
  'keys.closeOverlay': 'Close this overlay',
  'keys.goToPrices': 'Go to prices',
  'keys.goToPeriods': 'Go to periods',
  'keys.goToAccommodation': 'Go to the selected accommodation',
  'keys.goToUnits': 'Go to units and blocks',
  'keys.goToInquiries': 'Go to the enquiries',
  'keys.groupUnits': 'Units',
  'keys.focusBlockUnit': 'Block a unit — jump to the pitch field',

  /* ---------------------------------------------------------- price matrix */
  'matrix.noPeriods': 'No periods for this season yet. Add them on the Periods tab first.',
  'matrix.noDrafts': 'No accommodations with a draft for this season.',
  'matrix.nothingEditable': {
    one: '*Nothing editable per period on this card yet.*',
    other: '*Nothing editable per period on these {count} cards yet.*',
  } as Plural,
  'matrix.dayTariffsExplained': {
    one: 'They price by the night, with 1 day tariff of {rates}. There is no single price per period to show in a cell. Build bands from the period library to edit prices here; the day tariffs are left alone.',
    other: 'They price by the night, with {count} day tariffs of {rates}. There is no single price per period to show in a cell. Build bands from the period library to edit prices here; the day tariffs are left alone.',
  } as Plural,
  'matrix.noNightlyRates': {
    one: 'They carry {count} lifted rate, and it does not price single nights.',
    other: 'They carry {count} lifted rates, and none of them price single nights.',
  } as Plural,
  'matrix.save': 'Save',
  'matrix.readOnly': 'View only',
  'matrix.saving': 'Saving…',
  'matrix.unsaved': '{count} unsaved',
  'matrix.allSaved': 'saved',
  'matrix.fillDown': 'Fill down',
  'matrix.fillRight': 'Fill right',
  'matrix.nothingToSave': 'Nothing to save',
  'matrix.copied': 'Copied {rows} × {cols}',
  'matrix.copiedInAppOnly': 'Copied {rows} × {cols} — in-app only; the system clipboard needs https or localhost',
  'matrix.pasted': 'Pasted {rows} × {cols}',
  'matrix.filledDown': 'Filled down',
  'matrix.filledRight': 'Filled right',
  'matrix.adjusted': 'Adjusted by {percent}%',
  'matrix.accommodationColumn': 'Accommodation',
  'matrix.rowTitlePublished': '{label} (published)',
  'matrix.publishedChip': 'published',
  'matrix.periodColumnTitle': '{from} → {until}',
  'matrix.cell': '{row}, {period}',
  'matrix.hint': 'Press *?* for shortcuts. Paste a block straight out of a spreadsheet with *Ctrl/⌘+V*.',
  'matrix.keyMoveUp': 'Move up',
  'matrix.keyMoveDown': 'Move down',
  'matrix.keyMoveLeft': 'Move left',
  'matrix.keyMoveRight': 'Move right',
  'matrix.keyExtendUp': 'Extend selection up',
  'matrix.keyExtendDown': 'Extend selection down',
  'matrix.keyExtendLeft': 'Extend selection left',
  'matrix.keyExtendRight': 'Extend selection right',
  'matrix.keyNextCell': 'Next cell',
  'matrix.keyPreviousCell': 'Previous cell',
  'matrix.keyCommitDown': 'Commit and move down',
  'matrix.keyCommitUp': 'Commit and move up',
  'matrix.keyAbandonEdit': 'Abandon the edit',
  'matrix.keyCopy': 'Copy selection',
  'matrix.keyPaste': 'Paste (accepts a block from a spreadsheet)',
  'matrix.keyFillDown': 'Fill down from the top of the selection',
  'matrix.keyFillRight': 'Fill right from the left of the selection',
  'matrix.keySave': 'Save every change',
  'matrix.keyClear': 'Clear the selection',

  /* --------------------------------------------------------------- periods */
  'periods.heading': 'Periods',
  'periods.seedDutchHolidays': 'Add Dutch holidays',
  'periods.newPeriod': 'New period',
  'periods.intro': 'These become the columns of the price matrix. Public holidays are filled in for you.',
  'periods.emptyYear': 'Nothing for {year} yet.',
  'periods.columnPeriod': 'Period',
  'periods.columnHoliday': 'Holiday',
  'periods.columnDiscounts': 'Discounts may apply',
  'periods.columnKind': 'Kind',
  'periods.columnSource': 'Source',
  'periods.sameAsHoliday': 'same as the holiday',
  'periods.usedBy': 'used by {count}',
  'periods.edit': 'Edit',
  'periods.delete': 'Delete',
  'periods.kindPublicHoliday': 'public holiday',
  'periods.kindSchoolHoliday': 'school holiday',
  'periods.kindSeason': 'season',
  'periods.kindParkPeriod': 'park period',
  'periods.editHeading': 'Edit {label}',
  'periods.name': 'Name',
  'periods.namePlaceholder': 'Spring holiday',
  'periods.kind': 'Kind',
  'periods.holidayFrom': 'Holiday from',
  'periods.untilExclusive': '…until (exclusive)',
  'periods.wideWindow': 'Discounts from this period may be placed over a wider window',
  'periods.discountsFrom': 'Discounts from',
  'periods.wideWindowNote': 'Set this generously into the cheaper weeks either side: it keeps a long arrangement usable when a night or two falls outside the holiday. Avoid reaching into a dearer period — the generator warns about that.',
  'periods.save': 'Save period',
  'periods.cancel': 'Cancel',

  /* -------------------------------------------------------------- versions */
  'versions.chipTitle': {
    one: '{id} · {count} rate · updated {updated}',
    other: '{id} · {count} rates · updated {updated}',
  } as Plural,
  'versions.stateDraft': 'draft',
  'versions.statePublished': 'published',
  'versions.stateArchived': 'archived',
  /* An accommodation with no version cannot be priced at all, and until now the
     screen offered no way out: every action needed a version to be selected
     first, and cloning needed one to clone from. */
  'versions.noneYet': 'No version yet, so no guest can be quoted for this accommodation. The first one starts empty, in draft.',
  'versions.createFirst': 'Create the first version',
  'versions.createFirstHeading': 'First version for {label}',
  'versions.create': 'Create version',
  'versions.window': '{from} → {until}',
  'versions.windowsUnavailable': 'The dates each live version covers are not shown on the chips. Look a date up below instead.',
  'versions.windowTitle': 'Prices arrivals from {from} up to (but not including) {until}',
  'versions.revise': 'Correct this card',
  'versions.reviseHeading': 'Correct "{label}", in the same season',
  'versions.reviseLabel': '{label} (corrected)',
  'versions.reviseNote': 'A copy in the same season, with the same dates, for fixing mistakes in prices that are already live. Publishing it replaces the card it was taken from; nothing changes for a guest until you do.',
  'versions.rename': 'Rename…',
  'versions.renameHeading': 'Rename "{label}"',
  'versions.renameConfirm': 'Rename',
  'versions.renameNote': 'Only what this card is called. No price moves, and a live card stays live.',
  'versions.created': 'Made {when}',
  'versions.changed': 'last changed {when}',
  'versions.clone': 'Clone…',
  'versions.publish': 'Publish',
  'versions.publishForDates': 'Publish for dates…',
  'versions.publishForDatesHeading': 'Publish “{label}” for a window of arrivals',
  'versions.publishForDatesNote': 'Leave this to the season and the card covers the whole calendar year it was written for. State a window when a card runs from mid-season to mid-season, or when two cards share a year. Publishing over a window another card already claims retires that card.',
  'versions.publishFrom': 'First arrival priced',
  'versions.publishUntil': '…until (exclusive)',
  'versions.publishConfirm': 'Publish for these dates',
  'versions.alreadyLive': 'Already live',
  'versions.publishTitle': 'Make this the version guests are quoted from for the dates it covers',
  /* The reviewer's question, in the product: "which card serves 15 July 2027?"
     It is asked of the quote route, which resolves an arrival to a live table
     the same way a guest's quote does — so the answer is the real one, not this
     screen's reconstruction of the rule. */
  'versions.whichHeading': 'Which version prices an arrival?',
  'versions.whichNote': 'More than one version can be live at once, each for its own arrival dates. Give a date to see which card a guest arriving then is quoted from.',
  'versions.whichArrival': 'Arrival date',
  'versions.whichAsk': 'Look up',
  'versions.whichAnswer': '{label} prices an arrival on {date}.',
  'versions.whichServes': 'prices this date',
  'versions.archive': 'Archive',
  'versions.delete': 'Delete',
  'versions.deleteLiveTitle': 'A live version cannot be deleted; archive it first',
  'versions.deleteDraftTitle': 'Delete this draft',
  'versions.liveIsReadOnly': 'Live versions are read-only. Clone it, edit the clone, publish that.',
  'versions.cloneIntoNextSeason': 'Clone this version into next season',
  'versions.cloneHeading': 'Clone “{label}”',
  'versions.newId': 'New version id',
  'versions.label': 'Label',
  'versions.season': 'Season',
  'versions.shiftBy': 'Shift dates by',
  'versions.shift364': '364 days — same weekday next year',
  'versions.shift365': '365 days — same date next year',
  'versions.shiftNone': 'Not at all',
  'versions.shiftNote': '364 days is a whole number of weeks, so Saturday changeovers stay on Saturdays. 365 moves every changeover day by one.',
  'versions.dropLadder': 'Drop the length-of-stay ladder, to regenerate it fresh',
  'versions.createClone': 'Create clone',
  'versions.cancel': 'Cancel',
  'versions.idTaken': 'There is already a version called {id}. Give this one a different id.',
  'versions.windowBackwards': 'The window has to end after it starts. The last date is exclusive, so a card for one arrival day runs from that day to the next.',

  /* --------------------------------------------------------- accommodations */
  'accommodation.edit': 'Edit…',
  'accommodation.editHeading': 'Edit {label}',
  'accommodation.name': 'Name',
  'accommodation.namePlaceholder': 'Comfort pitch',
  'accommodation.kind': 'Kind',
  'accommodation.kindCamping': 'camping pitch',
  'accommodation.kindSeasonal': 'seasonal pitch',
  'accommodation.kindRental': 'rental',
  'accommodation.kindCustom': 'something else',
  'accommodation.identifierIs': 'Its identifier stays {id}.',
  'accommodation.attributesHeading': 'What it offers',
  'accommodation.attributesNote': 'Guests filter the website on these. A blank is not “no” — the pitch will not appear in a search for that thing at all.',
  'accommodation.noAttributes': 'This park has declared no attributes.',
  'accommodation.notStated': 'not stated',
  'accommodation.save': 'Save accommodation',
  'accommodation.cancel': 'Cancel',

  /* ----------------------------------------------------------------- quote */
  'quote.total': 'Total',
  'quote.explain': 'Open the rule behind this price',
  'quote.jumped': 'Opened {label}, the rule behind that price.',
  'quote.stayRange': '{nights} nights · {from} → {until}',
  'quote.noCombination': 'no legal combination of rates',
  'quote.perNight': 'Per night',
  'quote.averaged': 'averaged over the stay',
  'quote.ratesUsed': 'Rates used',
  'quote.placements': {
    one: '{count} placement',
    other: '{count} placements',
  } as Plural,
  'quote.status': 'Status',
  'quote.noConditions': 'no conditions',
  'quote.engineError': 'engine error',
  'quote.bookable': 'bookable',
  'quote.onRequest': 'on request',
  'quote.notAvailable': 'not available',
  'quote.noAvailability': 'No combination of rates covers these nights. The grid below shows which arrivals and lengths can be priced.',
  'quote.blockTitle': '{label} — {from}, {nights} nights, {price}',
  'quote.nightCount': {
    one: '1 night',
    other: '{count} nights',
  } as Plural,
  'quote.columnRate': 'Rate',
  'quote.columnFrom': 'From',
  'quote.columnNights': 'Nights',
  'quote.columnAmount': 'Amount',
  'rounding.label': 'Round prices to',
  'rounding.none': 'The nearest cent \u2014 whatever the rules add up to',
  'rounding.cents': 'The nearest cent',
  'rounding.tenCents': 'The nearest ten cents',
  'rounding.euro': 'The nearest whole euro',
  'rounding.psychological': 'The old calculator\u2019s scheme (retired)',
  'rounding.note': 'Applied once to the total for the stay, not to each rule. A week is not always seven rounded nights.',
  'copy.from': 'Copy rules from',
  'copy.choose': 'Choose an accommodation\u2026',
  'copy.sourceEmpty': 'That card has no rules yet.',
  'copy.selectAll': 'Select all',
  'copy.selectNone': 'Select none',
  'copy.needsQuantity': 'needs {keys}',
  'copy.willReplace': 'replaces one here',
  'copy.replacing': {
    one: 'One rule here has the same name and will be replaced.',
    other: '{count} rules here have the same name and will be replaced.',
  } as Plural,
  'copy.go': {
    one: 'Copy 1 rule',
    other: 'Copy {count} rules',
  } as Plural,
  'copy.datesNote': 'Dates come across exactly as they are. They were written for another accommodation, so check its windows as well as its prices.',
  'copy.copied': {
    one: '1 rule copied.',
    other: '{count} rules copied.',
  } as Plural,
  'copy.heading': 'Card settings',
  'quote.surcharges': 'Surcharges: {list}',
  'quote.rounding': 'Rounded to the nearest price the park quotes',

  /* ------------------------------------------------------------ price grid */
  'grid.pricing': 'Pricing {count} combinations…',
  'grid.view.total': 'Price for the stay',
  'grid.view.extra': 'Each extra night',
  'grid.view.perNight': 'Average per night',
  'grid.viewNote.total': 'What a guest pays for the whole stay.',
  'grid.viewNote.extra': 'What the nights beyond the previous length cost, each. This is where a package or a length discount shows up.',
  'grid.viewNote.perNight': 'The stay divided by its nights. This is the figure a channel reads, and the one it expects never to rise as a stay gets longer.',
  'grid.arrivalColumn': 'arrival date',
  'grid.losHeader': '{nights}n',
  'grid.stillPricing': 'pricing…',
  'grid.cellUnavailable': '{date}, {nights} nights — not available',
  'grid.cellPrice': '{date}, {nights} nights — {price} per night',
  'grid.cheaper': 'cheaper per night',
  'grid.dearer': 'dearer',

  /* ---------------------------------------------------- guests and extras */
  'guests.roleAdult': 'Adult',
  'guests.roleChild': 'Child',
  'guests.roleInfant': 'Infant',
  'guests.roleOther': 'Not a person',
  'guests.basisPerStay': 'per stay',
  'guests.basisPerNight': 'per night',
  'guests.basisPerPerson': 'per person',
  'guests.basisPerPersonPerNight': 'per person per night',

  'guests.categoriesHeading': 'Guest categories',
  'guests.categoriesNote': 'what this park counts, and what a channel counts',
  'guests.categoriesIntro': 'A category describes who is in a party; it does not have to be charged for. *Channel* is how an OTA counts one — several priced age bands may all be children to a channel.',
  'guests.columnName': 'Name',
  'guests.columnAges': 'Ages',
  'guests.columnChannel': 'Channel',
  'guests.columnAlsoBills': 'Also billed here',
  'guests.categoryAlsoBills': 'Other roles category {number} also bills',
  'guests.columnIncluded': 'In the base',
  'guests.categoryIncluded': 'How many of category {number} the base price already covers',
  'guests.columnTakesPlace': 'Takes a place',
  'guests.columnTestParty': 'Test party',
  'guests.categoryName': 'Name for category {number}',
  'guests.categoryAgeFrom': 'First age for category {number}',
  'guests.categoryAgeUntil': 'Last age for category {number}',
  'guests.categoryChannel': 'Channel role for category {number}',
  'guests.categoryCounts': 'Category {number} takes one of the included places',
  'guests.categoryTestCount': 'Test count for {label}',
  'guests.remove': 'Remove',
  'guests.noCategories': 'No guest categories. Nothing can be counted or charged per person.',
  'guests.addCategory': 'Add category',
  /* The label a freshly added category starts with: the operator's own first
     draft of a name, so it arrives in their language rather than in ours. */
  'guests.newCategoryLabel': 'Guest',
  'guests.channelSees': 'A channel sees',
  'guests.petCount': {
    one: '{count} pet',
    other: '{count} pets',
  } as Plural,
  'guests.counted': '{count} counted',
  'guests.overTheLimit': 'over the limit',
  'guests.tooManyPets': 'too many pets',

  'guests.occupancyHeading': 'Who fits',
  'guests.included': 'Included in the price',
  'guests.hardLimit': 'Hard limit',
  'guests.petsAllowed': 'Pets allowed',
  'guests.includedEqualsLimit': 'Included equals the limit, so nobody extra can be booked or charged.',
  'guests.noLimit': 'No limit set. Any party size will be priced.',
  'guests.setLimit': 'Set a limit',

  'guests.feesHeading': 'Charged on top',
  'guests.feeCount': {
    one: '{count} item',
    other: '{count} items',
  } as Plural,
  'guests.columnItem': 'Item',
  'guests.columnAmount': 'Amount',
  'guests.columnBasis': 'Basis',
  'guests.columnCounts': 'Counts',
  'guests.columnBeyondIncluded': 'Beyond included',
  'guests.columnOptional': 'Optional',
  'guests.feeName': 'Name for item {number}',
  'guests.feeAmount': 'Amount for item {number}',
  'guests.feeBasis': 'Basis for item {number}',
  'guests.feeQuantity': 'Counted quantity for item {number}',
  'guests.feeBeyondIncluded': 'Item {number} charges only beyond the included places',
  'guests.feeOptional': 'Item {number} is optional',
  'guests.everyoneCounted': 'everyone counted',
  'guests.noFees': 'Nothing charged on top.',
  'guests.addFee': 'Add item',
  /* Likewise a starting point, not a translation of stored data. */
  'guests.newFeeLabel': 'Extra person',
  'guests.beyondIncludedCount': 'The test party has {count} beyond the {included} included.',
  'guests.feesExplained': '*Beyond included* charges only the people past the included count — “two included, the third costs extra”. *Optional* items, such as towels and bed linen, are quoted but left out of the total until a guest picks them.',

  'guests.extrasOnTop': 'On top',
  'guests.extrasRate': 'Rate',
  'guests.extrasUnits': '×',
  'guests.extrasTotal': 'Total',
  'guests.extrasAccommodation': 'Accommodation',
  'guests.extrasTax': 'tax',
  'guests.extrasOptional': 'optional',
  'guests.extrasSubtotal': 'Extras',

  /* ------------------------------------------------------------- programme */
  'programme.noDiagnostics': 'Every rate does what its settings say. No inert tapers, no unreachable windows.',
  'programme.severityError': 'error',
  'programme.severityWarning': 'warning',
  'programme.severityInfo': 'info',
  'programme.rateNights': '{nights}n',
  'programme.taperWholeStay': 'taper whole-stay',
  'programme.taperPerNight': 'taper per-night',
  'programme.extends': 'extends',
  'programme.inert': 'inert',
  'programme.minTotal': 'min total',
  'programme.blocks': 'blocks',
  'programme.covers': 'Covers',
  'programme.coversValue': {
    one: '{count} night per placement',
    other: '{count} nights per placement',
  } as Plural,
  'programme.trimmedTo': 'May be trimmed to',
  'programme.trimmedToValue': '{nights} nights, still full price',
  'programme.active': 'Active',
  'programme.activeValue': '{from} → {until}',
  'programme.placementWindows': 'Placement windows',
  'programme.basePrice': 'Base price',
  'programme.perUnit': 'Per {unit}',
  'programme.extensionStep': 'Extension step',
  'programme.extensionStepValue': '{nights} night(s) at {price}',
  'programme.maxSteps': 'Max steps',
  'programme.resumesAfterGap': 'Resumes after a gap',
  'programme.resumesAfterGapValue': 'yes — the taper continues across an interruption',
  'programme.taper': 'Taper',
  'programme.taperValue': '{mode}, factor {factor} after {nights} nights',
  'programme.taperModeWholeStay': 'whole stay',
  'programme.taperModePerNight': 'per night',
  'programme.convergesTo': 'Converges to',
  'programme.bookingTotal': 'Booking total must reach',
  'programme.stayLength': 'Stay length',
  'programme.stayLengthValue': '{min}–{max} nights',
  'programme.arriveOn': 'Arrive on',
  'programme.departOn': 'Depart on',
  'programme.availability': 'Availability',
  'programme.availabilityClosed': 'closed',
  'programme.availabilityOnRequest': 'on request',
  'programme.invalidJson': 'That is not valid JSON: {message}',
  'programme.applied': 'Applied.',
  'programme.applyChanges': 'Apply changes',
  'programme.revert': 'Revert',
  'programme.edited': 'edited',
  'programme.unchanged': 'unchanged',
  'programme.json': 'Programme JSON',

  /* ---------------------------------------------------------------- ladder */
  'ladder.heading': 'Length-of-stay ladder',
  'ladder.note': 'a discount off the nightly price',
  'ladder.columnName': 'Name',
  'ladder.columnNights': 'Nights',
  'ladder.columnPrice': 'Price',
  'ladder.columnArrivals': 'Arrivals',
  'ladder.entryNamePlaceholder': '{nights} nights',
  'ladder.entryName': 'Name for entry {number}',
  'ladder.entryNights': 'Nights for entry {number}',
  'ladder.entryBasis': 'Price basis for entry {number}',
  'ladder.entryBasisPercent': '% off',
  'ladder.entryBasisFlat': 'flat €',
  'ladder.entryPrice': 'Total price for entry {number}',
  'ladder.entryDiscount': 'Discount for entry {number}',
  'ladder.entryFirstArrival': 'First arrival for entry {number}',
  'ladder.entryLastArrival': 'Last arrival for entry {number}',
  'ladder.wholePeriod': 'whole period',
  'ladder.duplicate': 'Duplicate',
  'ladder.duplicateTitle': 'Duplicate (Alt+D)',
  'ladder.remove': 'Remove',
  'ladder.copySuffix': '{label} (copy)',
  'ladder.noEntries': 'No length-of-stay entries. The card prices every stay by the night.',
  'ladder.addEntry': 'Add entry',
  'ladder.preview': 'Preview changes',
  'ladder.working': 'Working…',
  'ladder.shortcutHint': 'Alt+N add · Alt+D duplicate · ⌘↵ preview',
  'ladder.observedHeading': 'What this card already charges',
  'ladder.observedNote': 'Read back from the rates already on this card. A wide spread at one length means several different products share it, and flattening them would reprice some.',
  'ladder.observedNights': 'Nights',
  'ladder.observedTypical': 'Typical',
  'ladder.observedSpread': 'Spread',
  'ladder.observedFrom': 'From',
  'ladder.observedWide': 'wide',
  'ladder.observedSamples': {
    one: '{count} rate',
    other: '{count} rates',
  } as Plural,
  'ladder.leakageHeading': 'Discounts reaching into a dearer period',
  'ladder.leakageNote': 'These are priced off cheap nights and can be placed in an expensive period, where they undercut it outright.',
  'ladder.leakageRate': 'Rate',
  'ladder.leakagePricedFrom': 'Priced from',
  'ladder.leakageReaches': 'Reaches',
  'ladder.leakageDiscount': 'Discount',
  'ladder.leakageThatPeriod': 'That period',
  'ladder.perNight': '{price}/night',
  'ladder.reachesInto': '{label} at {price}/night',
  'ladder.proposedHeading': 'Proposed changes',
  'ladder.nothingToChange': 'nothing to change',
  'ladder.acceptAllUnedited': 'Accept all unedited',
  'ladder.clear': 'Clear',
  'ladder.apply': 'Apply',
  'ladder.applyCount': 'Apply {count}',
  'ladder.applyBlocked': 'A published version cannot be edited',
  'ladder.alreadyMatches': 'The ladder already matches these steps.',
  'ladder.changeRate': 'Rate',
  'ladder.changeKind': 'Change',
  'ladder.changeBefore': 'Before',
  'ladder.changeAfter': 'After',
  'ladder.accept': 'Accept {label}',
  'ladder.handEdited': 'hand-edited',
  'ladder.handEditedTitle': 'Edited by hand — accepting will overwrite it',
  'ladder.kindAdded': 'added',
  'ladder.kindRemoved': 'removed',
  'ladder.kindChanged': 'changed',
  'ladder.keyPreview': 'Preview the generated changes',
  'ladder.keyAddEntry': 'Add a length-of-stay entry',
  'ladder.keyDuplicateEntry': 'Duplicate the current entry',
  'ladder.keyRemoveEntry': 'Remove the current entry',
  'ladder.keyNextEntry': 'Next entry',
  'ladder.keyPreviousEntry': 'Previous entry',

  /* ------------------------------------------------------- published rates */
  'published.heading': 'Publish a rate grid',
  'published.note': 'what a channel manager would receive',
  'published.firstArrival': 'First arrival',
  'published.days': 'Days',
  'published.lengths': 'Lengths',
  'published.lengthsValue': '{lengths} nights',
  'published.materialise': 'Materialise',
  'published.materialising': 'Materialising…',
  'published.progress': '{done} of {total} cells.',
  'published.cellsHeading': 'Published cells',
  'published.rowCount': '{count} rows',
  'published.cellsNote': 'One row per arrival and length, as a channel receives it.',
  'published.columnArrival': 'Arrival',
  'published.columnLos': 'LOS',
  'published.columnPrice': 'Price',
  'published.columnStatus': 'Status',
  'published.columnSource': 'Source',
  'published.truncated': 'Showing the first 400 of {total}.',
  'published.bandsHeading': 'Stored as bands',
  'published.bandCount': '{count} bands',
  'published.bandsNote': 'Consecutive arrivals sharing a price collapse into one band. Expanding them reproduces every cell exactly.',
  'published.columnFrom': 'From',
  'published.columnTo': 'To',
  'published.statCells': 'Cells published',
  'published.statCellsNote': '{bookable} bookable · {closed} closed',
  'published.statStoredAs': 'Stored as',
  'published.statStoredAsNote': 'bands — {percent}% of the cell count',
  'published.statVerified': 'Verified',
  'published.statDrifted': '{count} drifted',
  'published.statNoDrift': '{count} re-priced, 0 drift',
  'published.statVerifiedNote': 'every sampled cell re-checked against the engine',
  'published.statCheapest': 'Cheapest stay',
  'published.statCheapestNote': '{seconds}s to compute',

  /* --------------------------------------------------------------- units */
  'units.heading': 'Units',
  'units.intro': 'One row per pitch or rental, in the order the park map reads them: terrain first, then number. A unit missing here cannot be sold, and a unit blocked here is not offered to anybody.',
  'units.loading': 'Reading the park…',
  'units.chooseAccommodation': 'Choose an accommodation to see its units.',
  'units.emptyForAccommodation': 'No units for {label} yet, so nothing of this type can be sold. Add one, or generate a whole numbered terrain at once.',
  'units.count': {
    one: '{count} unit',
    other: '{count} units',
  } as Plural,
  'units.activeCount': '{count} for sale',
  'units.terrain': 'Terrain',
  'units.allTerrains': 'Every terrain',
  'units.noTerrain': 'no terrain',
  'units.columnUnit': 'Unit',
  'units.columnTerrain': 'Terrain',
  'units.columnNumber': 'No.',
  'units.columnGuestMayPick': 'Guest may pick',
  'units.columnStatus': 'Status',
  'units.columnBlocked': 'Blocked',
  'units.statusActive': 'for sale',
  'units.statusRetired': 'retired',
  'units.yes': 'yes',
  'units.no': 'no',
  'units.blockCount': {
    one: '{count} block',
    other: '{count} blocks',
  } as Plural,
  'units.noBlocks': 'nothing ahead',
  'units.block': 'Block…',
  'units.edit': 'Edit',
  'units.delete': 'Delete',
  'units.deleteThis': 'Delete this unit',
  'units.truncated': 'Showing the first {shown} of {total}. Pick a terrain to see the rest.',
  'units.addUnit': 'Add unit',
  'units.generateRange': 'Generate a range…',
  'units.terrains': 'Terrains…',
  'units.close': 'Close',
  'units.save': 'Save unit',
  'units.cancel': 'Cancel',

  /* the block form — the most-used thing on the screen */
  'units.blockHeading': 'Block a unit',
  'units.blockNote': 'the website respects it immediately',
  'units.blockUnit': 'Unit',
  'units.blockUnitPlaceholder': 'Berkenlaan 12',
  'units.blockFrom': 'First night blocked',
  'units.blockUntil': '…until (exclusive)',
  'units.blockReason': 'Reason',
  'units.blockNoteField': 'Note',
  'units.blockNotePlaceholder': 'New shower block being fitted',
  'units.blockSubmit': 'Block these nights',
  'units.blockSummary': '{unit} on {terrain} · {nights}',
  'units.blockOtherType': 'It belongs to another accommodation than the one selected.',
  'units.blockNeedsUnit': 'Type the pitch as reception says it.',
  'units.blockNoSuchUnit': 'No unit in this park is called “{text}”. Check the spelling, or add it below.',
  'units.blockNeedsNights': 'The last date is exclusive, so it has to be after the first: one night blocked runs from a day to the next.',
  'units.blockNothingToBlock': 'This accommodation has no units yet, so there is nothing of it to block.',
  'units.reasonBlocked': 'blocked',
  'units.reasonContract': 'sold for the year',
  'units.reasonMaintenance': 'maintenance',
  'units.reasonClosed': 'closed',
  'units.reasonOwner': 'the owner is using it',

  /* the block list */
  'units.blocksHeading': 'What is blocked',
  'units.blocksForUnit': 'What is blocked on {unit}',
  'units.showEveryUnit': 'Every unit',
  'units.includePast': 'Include blocks that have already ended',
  'units.noBlocksAhead': 'Nothing is blocked from today onwards, so every unit of this type is for sale on every date its prices cover.',
  'units.noBlocksAtAll': 'Nothing has ever been blocked here.',
  'units.columnNights': 'Nights',
  'units.columnLength': 'Length',
  'units.columnReason': 'Reason',
  'units.removeBlock': 'Remove',

  /* one unit */
  'units.newHeading': 'New unit',
  'units.editHeading': 'Edit {label}',
  'units.address': 'Address',
  'units.addressPlaceholder': 'Berkenlaan 12',
  'units.addressNote': 'It has to be unique in the park. A unit with a name of its own shows that instead.',
  'units.name': 'Name of its own',
  'units.namePlaceholder': 'De Eik',
  'units.number': 'Number',
  'units.noteField': 'Note',
  'units.notePlaceholder': 'Sloping; not for a large caravan',
  'units.active': 'For sale. A retired unit keeps its history and is never offered.',
  'units.selectable': 'A guest may ask for this unit specifically, not just its type',
  'units.identifierIs': 'It will be stored as {id}, and cannot change afterwards.',
  'units.needsAName': 'Give it an address, or a name of its own. Without one there is nothing to call it.',
  'units.addressTaken': '“{address}” is already the address of {unit}. Two units in one park cannot share one.',
  'units.identifierTaken': 'A unit is already stored as {id}. Give this one a number of its own.',

  /* a whole terrain at once */
  'units.generateHeading': 'Generate a numbered range',
  'units.generateNote': 'An address already in use is reported and left alone; the rest of the range still lands.',
  'units.generateFrom': 'From number',
  'units.generateTo': 'To number',
  'units.generatePrefix': 'Address prefix',
  'units.generatePrefixPlaceholder': 'the terrain’s name',
  'units.generateCount': {
    one: 'This creates 1 unit, addressed {example}.',
    other: 'This creates {count} units, the first addressed {example}.',
  } as Plural,
  'units.generateBackwards': 'The last number has to be at least the first.',
  'units.generateTooMany': 'That is more than 500 units in one go. Split the range.',
  'units.generate': 'Generate',
  'units.someTaken': {
    one: '1 address was already in use and was left exactly as it was: {list}',
    other: '{count} addresses were already in use and were left exactly as they were: {list}',
  } as Plural,

  /* terrains */
  'units.terrainsHeading': 'Terrains',
  'units.terrainsNote': 'The parts of the park a unit sits in, and the order a map reads them in. A terrain’s name becomes the address of every unit generated in it, and two terrains in one park cannot share a name.',
  'units.noTerrains': 'This park is not divided into terrains. A unit can sit in none.',
  'units.terrainName': 'Name',
  'units.terrainNamePlaceholder': 'Berkenlaan',
  'units.terrainOrder': 'Order',
  'units.addTerrain': 'Add terrain',
  'units.saveTerrain': 'Save terrain',
  'units.terrainNameTaken': 'This park already has a terrain called “{label}”.',

  /* ------------------------------------------------------------- enquiries */
  'inquiries.heading': 'Enquiries',
  'inquiries.intro': 'What the website’s enquiry form sent. Nothing is held and no price is promised; somebody here answers by hand.',
  'inquiries.loading': 'Reading the enquiries…',
  'inquiries.empty': 'No enquiries yet. One appears here the moment somebody sends the form on the website.',
  'inquiries.emptyForStatus': 'No enquiries are marked “{status}”.',
  'inquiries.count': {
    one: '{count} enquiry',
    other: '{count} enquiries',
  } as Plural,
  'inquiries.newCount': '{count} not yet answered',
  'inquiries.filterStatus': 'Status',
  'inquiries.everyStatus': 'Every status',
  'inquiries.columnGuest': 'Guest',
  'inquiries.columnAccommodation': 'Accommodation',
  'inquiries.columnStay': 'Arrival',
  'inquiries.columnPrice': 'Price',
  'inquiries.columnStatus': 'Status',
  'inquiries.received': 'received {date}',
  'inquiries.noPrice': 'no price',
  'inquiries.open': 'Open',
  'inquiries.close': 'Close',
  'inquiries.statusNew': 'new',
  'inquiries.statusRead': 'read',
  'inquiries.statusAnswered': 'answered',
  'inquiries.statusBooked': 'booked',
  'inquiries.statusDeclined': 'declined',
  'inquiries.statusClosed': 'closed',
  'inquiries.statusSpam': 'spam',
  'inquiries.setStatus': 'Mark as…',
  'inquiries.detailHeading': 'Enquiry from {name}',
  'inquiries.guestHeading': 'Who wrote',
  'inquiries.name': 'Name',
  'inquiries.email': 'Email',
  'inquiries.phone': 'Phone',
  'inquiries.address': 'Address',
  'inquiries.licencePlate': 'Licence plate',
  'inquiries.commentHeading': 'What they wrote',
  'inquiries.notGiven': 'not given',
  'inquiries.stayHeading': 'What they asked for',
  'inquiries.accommodation': 'Accommodation',
  'inquiries.arrival': 'Arrival',
  'inquiries.departure': 'Departure',
  'inquiries.nights': 'Nights',
  'inquiries.party': 'Party',
  'inquiries.priceHeading': 'Price',
  'inquiries.priceComputed': 'Computed here',
  'inquiries.priceQuoted': 'Shown on the website',
  'inquiries.pricedBy': 'Priced by',
  'inquiries.availability': 'Availability then',
  'inquiries.wereFree': { one: '1 was free', other: '{count} were free' },
  'inquiries.notCounted': 'this park does not count units of this type',
  'inquiries.wasFree': 'something of this type was free',
  'inquiries.wasNotFree': 'nothing of this type was free',
  'inquiries.priceUnknown': 'No price was computed: nothing live covered those dates when the enquiry arrived. Publish a card for them and the figure can be worked out on the Prices tab.',
  'inquiries.priceMismatch': 'The website showed a different figure from the one computed here — the guest’s page had gone stale. Answer with the computed one.',
  'inquiries.provenance': 'Came in through {channel} · notification {mail} · sent from {address}',
  'inquiries.noMail': 'not sent',
  /* ------------------------------------------ what the park actually sent */
  'inquiries.mailsHeading': 'What we sent',
  'inquiries.guestPage': "Open the guest's own page for this enquiry",
  'inquiries.guestPageUnset': "No site address is configured, so the mails carried no link to a guest page. Set it under Settings.",
  'inquiries.mailsLoading': 'Fetching the messages…',
  'inquiries.mailsFailed': 'The messages could not be fetched.',
  'inquiries.mailsNone': 'Nothing was sent about this enquiry.',
  'inquiries.mailToPark': 'To reception',
  'inquiries.mailToGuest': 'To the guest',
  'inquiries.mailSent': 'sent',
  'inquiries.mailTo': 'To',
  'inquiries.mailSubject': 'Subject',
  'inquiries.mailWhen': 'Sent',

  'inquiries.privacyNote': 'These details are shown here and nowhere else. A shared link or a screenshot of the address bar cannot carry them.',

  // --- Stay rules -----------------------------------------------------------
  'app.tabRules': 'Stay rules',
  'keys.goToRules': 'Stay rules',
  'rules.heading': 'What a stay has to look like',
  'rules.intro': 'These say where the website should stop pricing and let somebody at the park answer instead — a length nobody sells online, a changeover day, a week offered only by arrangement.',

  'rules.thresholdHeading': 'Long stays on request',
  'rules.thresholdIntro': 'Above this length no price is shown and the guest is asked to write.',
  'rules.thresholdNights': 'Longer than (nights)',
  'rules.addThreshold': 'Add threshold',
  'rules.thresholdPreview': 'A stay of more than {nights} nights on {accommodation}, arriving in {year}, will show its price and ask the guest to contact the park.',
  'rules.thresholdLabel': 'Ask us about a seasonal pitch',
  'rules.thresholdNote': 'Stays over {nights} nights are arranged by hand, not sold online.',
  'rules.badThreshold': 'Give a length between 1 and 365 nights.',

  'rules.showGeneral': 'Add a rule',
  'rules.hideGeneral': 'Cancel',
  'rules.generalHeading': 'New rule',
  'rules.editing': 'Editing {id}',
  'rules.appliesTo': 'Applies to',
  'rules.everyType': 'every type',
  'rules.from': 'Covers stays from',
  'rules.until': 'Covers stays until',
  'rules.windowNote': 'The dates this rule watches. An arrival rule is judged on the ARRIVAL date, a departure rule on the DEPARTURE date. A stay that passes straight through, arriving before and leaving after, is not affected.',
  'rules.minNights': 'At least (nights)',
  'rules.maxNights': 'At most (nights)',
  'rules.effect': 'Then',
  'rules.effectBlock': 'Blocked \u2014 not possible',
  'rules.effectContact': 'On request \u2014 no price',
  'rules.effectIndicative': 'Target price \u2014 shown, confirmed by us',
  'rules.effectBlockNote': 'The stay cannot be had, and there is nothing to ask about. The guest is told why, and no price is shown.',
  'rules.effectContactNote': 'NO PRICE IS SHOWN and the guest is asked to write for a quote. Use this where the figure would vary, or where you want to be able to decline.',
  'rules.effectIndicativeNote': 'The price IS shown, marked as an indication, and the guest still writes rather than books. Use this where the figure is right and you want the last word.',
  'rules.arriveOn': 'Arrive only on',
  'rules.departOn': 'Leave only on',
  'rules.daysNote': 'Leave a row empty to allow every day. Ticking all seven means the same thing.',
  'rules.label': 'What the guest reads',
  'rules.labelNote': 'Shown on the website beside the price. This is the park speaking, so write it the way reception would say it on the phone.',
  'rules.note': 'Note for the office',
  'rules.addGeneral': 'Add condition',
  'rules.saveEdit': 'Save condition',
  'rules.cancelEdit': 'Cancel',
  'rules.badDates': 'Both dates must be real dates.',
  'rules.badOrder': 'The second date has to be after the first.',
  'rules.badNights': 'A length must be between 1 and 365 nights.',
  'rules.badRange': '"At most" cannot be less than "at least".',

  'rules.listHeading': { one: '{count} condition in force', other: '{count} conditions in force' },
  'rules.none': 'None. Every stay the tariff can price is offered directly.',
  'rules.colWhat': 'Condition',
  'rules.colWindow': 'Arrivals',
  'rules.colGuestSees': 'What the guest reads',
  'rules.noLabel': 'nothing — add a sentence',
  'rules.edit': 'Edit',
  'rules.remove': 'Remove',
  'rules.orderNote': 'Where two conditions cover one stay, the strictest wins.',
  'rules.saved': 'Condition saved.',
  'rules.removed': 'Condition removed.',

  'rules.sentence': '{what} on {accommodation}: {effect}',
  'rules.saysOver': 'a stay longer than {max} nights',
  'rules.saysUnder': 'a stay shorter than {min} nights',
  'rules.saysBetween': 'a stay outside {min}\u2013{max} nights',
  'rules.saysArriveOn': 'arriving other than {days}',
  'rules.saysDepartOn': 'leaving other than {days}',
  'rules.saysAnyStay': 'any stay',
  'rules.join': ', or ',
  'rules.sun': 'Sun',
  'rules.mon': 'Mon',
  'rules.tue': 'Tue',
  'rules.wed': 'Wed',
  'rules.thu': 'Thu',
  'rules.fri': 'Fri',
  'rules.sat': 'Sat',

  // --- Park settings --------------------------------------------------------
  'app.tabSettings': 'Settings',
  'app.tabAccounts': 'Accounts',
  /* ------------------------------------------------------------- accounts */
  'accounts.heading': 'Who can sign in',
  'accounts.note': 'Each account gets only what it needs.',
  'accounts.loading': 'Loading accounts\u2026',
  'accounts.add': 'New account',
  'accounts.newHeading': 'A new account',
  'accounts.login': 'Login',
  'accounts.label': 'Name',
  'accounts.password': 'Password',
  'accounts.passwordNote': 'At least 12 characters. They can change it once signed in.',
  'accounts.mayDo': 'May do',
  'accounts.presetReception': 'Fill in: reception',
  'accounts.create': 'Create account',
  'accounts.cancel': 'Cancel',
  'accounts.created': 'Account created.',
  'accounts.saved': 'Saved.',
  'accounts.state': 'State',
  'accounts.active': 'Active',
  'accounts.you': 'You',
  'accounts.disable': 'Disable',
  'accounts.enable': 'Allow again',
  'accounts.disabledOn': 'Disabled {when}',
  'accounts.disableNote': 'Disabled, not deleted. The account keeps its name in the change log.',
  'accounts.badLogin': 'A login needs at least 3 characters.',
  'accounts.badPassword': 'A password needs at least 12 characters.',
  'capability.site.register': 'Register a website that shows prices',
  'capability.catalogue.read': 'See prices, accommodations and occupancy',
  'capability.log.read': 'Read the change log',
  'capability.inquiry.read': 'Read enquiries',
  'capability.inquiry.write': 'Mark enquiries as answered',
  'capability.accommodation.write': 'Add and edit accommodations',
  'capability.version.write': 'Edit price cards',
  'capability.version.publish': 'Publish prices to the website',
  'capability.unit.write': 'Add and edit units',
  'capability.block.write': 'Change occupancy',
  'capability.rule.write': 'Change stay rules',
  'capability.park.write': 'Change park settings',
  'capability.admin': 'Everything, including accounts',
  'capability.park.any': 'Every park, including future ones',
  'keys.goToSettings': 'Settings',
  'settings.loading': 'Reading the settings\u2026',
  'settings.heading': 'This park',
  'settings.intro': 'Who the park is, and how values are written on a screen. Nothing here changes what is stored or sent.',

  'settings.parkHeading': 'The park',
  'settings.inquiryEmail': 'Enquiries go to',
  'settings.inquiryEmailNote': 'The mailbox that is told when a guest asks about a stay. An address with a typo does not fail — it just means nobody at the park hears about an enquiry.',
  'settings.replyWithin': 'Usually reply within (hours)',
  'settings.replyWithinNote': 'Shown in the enquiry mail and on the page the guest lands on. Leave empty to say nothing about timing.',
  'settings.defaultCountry': 'Country',
  'settings.defaultLocale': 'Language guests see',
  'settings.localeUnset': 'not set \u2014 English',
  'settings.defaultLocaleNote': 'The language a guest page opens in, and the one the enquiry mail is written in when the guest\u2019s browser did not say. A guest who chose a language always gets that one instead.',

  'settings.displayHeading': 'How things are written',
  'settings.displayNote': 'Display only.',
  'settings.dateFormat': 'Dates',
  'settings.dateLocale': 'follow the language',
  'settings.dateDmy': 'day first',
  'settings.dateMdy': 'month first',
  'settings.dateIso': 'year-month-day',
  'settings.timeFormat': 'Clock',
  'settings.timeZone': 'Timezone',
  'settings.timeZoneNote': 'Used for timestamps \u2014 when an enquiry arrived, when a table was saved. A date is never affected: an arrival on the 3rd is the 3rd everywhere.',
  'settings.currency': 'Currency',
  'settings.currencyNote': 'A label on the amounts. Changing it converts nothing — prices have to be entered again in the new currency.',
  'settings.preview': 'A stay arriving {date}, saved at {stamp}, costing {amount}.',
  'settings.save': 'Save settings',
  'settings.saved': 'Settings saved.',
  'settings.badEmail': 'That does not look like an email address.',
  'settings.badCurrency': 'A currency is a three-letter code, like EUR or CHF.',
  'settings.passwordHeading': 'Your password',
  'settings.passwordNote': 'Changing it signs out every other browser and device this account is open on. This screen stays signed in.',
  'settings.passwordCurrent': 'Current password',
  'settings.passwordNext': 'New password',
  'settings.passwordAgain': 'New password again',
  'settings.passwordRule': 'At least 12 characters. A few ordinary words you can remember are stronger than one short word with a digit on the end.',
  'settings.passwordChange': 'Change password',
  'settings.passwordChanged': 'Password changed. Every other session has been signed out.',
  'settings.passwordMismatch': 'The two new passwords are not the same.',
  'settings.passwordShort': 'A password of at least 12 characters is required.',
  'app.languageAuto': 'Browser ({lang})',
  'app.scheme': 'Appearance',
  'app.schemeSystem': 'Device',
  'app.schemeLight': 'Light',
  'app.schemeDark': 'Dark',

  // --- Signing in -----------------------------------------------------------
  'signin.heading': 'Sign in',
  'signin.intro': 'This is the park\u2019s tariff back office. Everything here is shared with whoever else works on prices.',
  'signin.setupHeading': 'Create the first account',
  'signin.setupIntro': 'Nobody has an account on this park yet. The first one is created here, and only once.',
  'signin.login': 'Login name',
  'signin.password': 'Password',
  'signin.confirm': 'Password again',
  'signin.passwordNote': 'At least 12 characters. A phrase you will remember is stronger than a short word with a digit on the end.',
  'signin.submit': 'Sign in',
  'signin.create': 'Create account and sign in',
  'signin.errShort': 'That password is shorter than 12 characters.',
  'signin.errMismatch': 'The two passwords are not the same.',
  'signin.errFailed': 'That did not work. Try again.',
  'signin.signOut': 'Sign out',
  'signin.signedOut': 'You are signed out.',
  'programme.extensionStepPrice': 'Each further {nights}N',
  'app.revert': 'Undo',
  'app.rateUnsaved': 'Not saved yet.',
  'matrix.rollForward': 'Copy {from} to {to}',
  'matrix.rollForwardNote': 'Makes a draft of every {from} card, dated a year on. Nothing goes live until you publish it, and the {from} prices are untouched.',
  'matrix.rolledForward': { one: '{count} card copied. Change what moved, then publish.', other: '{count} cards copied. Change what moved, then publish.' },
  'matrix.sharedCell': 'One rate prices this period and at least one other. Changing it here changes them together.',
  'matrix.sharedLegend': 'A marked cell is priced by a rate that also covers another period — editing it moves both.',
  'matrix.rowNoNightly': 'no night price',
  'matrix.rowNoNightlyTitle': 'This card has no nightly band — it sells by the week or by arrangement. Typing a night price here ADDS one, and the engine will use it wherever it works out cheaper: a €80 night turns a €795 week into €560.',
  'app.programmeSourceAdvanced': 'advanced',
  'app.programmeSourceNote': 'The card as the software stores it. Use the panel above for prices; this is for changes that panel cannot express yet. A mistake here is refused rather than stored.',

  // --- What a rate is, in the operator's terms ------------------------------
  'programme.groupNightly': 'What a night costs',
  'programme.groupNightlyNote': 'The base layer, through the year. Everything else is priced against these.',
  'programme.groupLadder': 'Longer stays',
  'programme.groupLadderNote': 'A week or a fortnight priced below the same number of single nights. The guest is charged whichever works out cheaper.',
  'programme.groupArrangement': 'Packages on fixed dates',
  'programme.groupArrangementNote': 'A named stay over particular days \u2014 Easter, Ascension, the May holiday. Priced as a whole, not per night.',
  'programme.groupOther': 'Everything else',
  'programme.groupOtherNote': 'Rates that do not fall into the three above. A lifted card puts its odder rows here.',
  'programme.extendsWhat': 'A stay longer than this rate covers continues at a stated price per extra night, rather than falling back to the nightly rate.',
  'programme.taperWhat': 'The longer the stay, the less each further night costs \u2014 the discount deepens as it goes.',
  'programme.inertWhat': 'This taper never takes effect: nothing reaches the length at which it would start. It is doing nothing.',
  'programme.minTotalWhat': 'Only offered when the whole booking is worth at least a stated amount.',
  'programme.blocksWhat': 'Closed. This rate refuses the stay rather than pricing it.',
  'units.reasonSeasonal': 'closed for the season',
  'units.reasonOther': 'other',
  'matrix.midweekNights': 'Sun–Thu',
  'matrix.weekendNights': 'Fri–Sat',
  'matrix.cellNights': '{row}, {period}, {nights}',
  'matrix.splitWeek': 'Weekend prices',
  'matrix.splitWeekNote': 'A night is named by the day it starts, so Friday and Saturday nights are the weekend ones. Turning this on gives every period two prices; turning it off again shows only the mid-week one and keeps both.',

  // --- The night calendar ---------------------------------------------------
  'calendar.tab': 'Calendar',
  'calendar.selectWeekday': 'Select every:',
  'calendar.day0': 'Mon',
  'calendar.day1': 'Tue',
  'calendar.day2': 'Wed',
  'calendar.day3': 'Thu',
  'calendar.day4': 'Fri',
  'calendar.day5': 'Sat',
  'calendar.day6': 'Sun',
  'calendar.clear': 'Clear selection',
  'calendar.overridden': 'typed over the card\u2019s own price',
  'calendar.restore': 'Give these nights back to the card',
  'calendar.restoreHint': 'Leave the price empty and press this to remove a price you typed over these nights. They go back to whatever the card prices them at. Typing 0 does something else: it makes the night free.',
  'calendar.priceFor': { one: 'Price for {count} selected night', other: 'Price for {count} selected nights' },
  'calendar.apply': 'Set this price',
  'calendar.applyNote': 'Writes one rate for the nights you picked \u2014 the dates they span, and only the weekdays among them. It takes precedence over the season band, dearer or cheaper.',
  'calendar.pricing': 'Working out what each night costs\u2026',
  'calendar.noPrice': 'nothing prices this night',
  'calendar.noPeriod': 'in no period',
  'calendar.inPeriod': 'in {period}',
  'calendar.pricedBy': 'priced by {rate}',
  'calendar.rateLabel': '{from} \u2013 {until}',
  'calendar.heading': 'Every night of the season',
  'calendar.intro': 'What a guest would pay for one night starting on each day. Click nights to select them \u2014 hold Shift for a range \u2014 or use the buttons to take every Saturday.',

  'calendar.modeLabel': 'How you want to work:',
  'calendar.modeSelect': 'Pick nights, one price',
  'calendar.modeType': 'Type a price per night',
  'calendar.typeNote': 'Type into the nights themselves; Tab moves to the next. Nothing is written until you save.',
  'calendar.saveTyped': { one: 'Save {count} night', other: 'Save {count} nights' },
  'calendar.discardTyped': 'Discard typing',

  // -------------------------------------------------------------------------
  // The structural rate editor. Sentences, not fields.
  // -------------------------------------------------------------------------

  'day.long.monday': 'Monday',
  'day.long.tuesday': 'Tuesday',
  'day.long.wednesday': 'Wednesday',
  'day.long.thursday': 'Thursday',
  'day.long.friday': 'Friday',
  'day.long.saturday': 'Saturday',
  'day.long.sunday': 'Sunday',
  'day.short.monday': 'Mon',
  'day.short.tuesday': 'Tue',
  'day.short.wednesday': 'Wed',
  'day.short.thursday': 'Thu',
  'day.short.friday': 'Fri',
  'day.short.saturday': 'Sat',
  'day.short.sunday': 'Sun',
  'shape.anyDay': 'Any day.',

  'shape.sumCovers': { one: 'Covers {nights} night', other: 'Covers {nights} nights' },
  'shape.sumTrimmed': 'may be shortened to {nights} at the same price',
  'shape.sumWindow': 'for nights from {from} to {until}',
  'shape.sumStartsOn': 'only starts on {days}',
  'shape.sumStayStartsOn': 'the stay itself must start on {days}',
  'shape.sumExtends': 'repeats in steps of {nights} nights',
  'shape.sumExtendsCapped': 'repeats in steps of {nights} nights, at most {times} times',
  'shape.sumTaperPerNight': 'after {nights} nights each further night costs {pct}% of the one before',
  'shape.sumTaperWholeStay': 'after {nights} nights the whole run drops to {pct}% for each further night',
  'shape.sumFloor': 'never below {price} a night',
  'shape.sumStayLength': 'only for stays of {min} to {max} nights',
  'shape.sumBlocks': 'blocks these nights entirely',
  'shape.sumRefuses': 'is priced but cannot be booked',
  'shape.sumNeedsStaff': 'reception confirms it by hand',

  'shape.legendCovers': 'What one booking of this covers',
  'shape.nights': 'Nights covered',
  'shape.trimmable': 'A shorter stay may still use it',
  'shape.trimmableHint': 'For a package that runs to the end of the season: the last guest gets fewer nights and pays the same.',
  'shape.trimmedTo': 'As short as (nights)',

  'shape.legendWhen': 'When it may be used',
  'shape.firstNight': 'First night',
  'shape.lastNight': 'Last night',
  'shape.lastNightHint': 'The last night a guest sleeps under this offer, not the departure day.',
  'shape.fillFromPeriod': 'Fill these dates from a period',
  'shape.choosePeriod': 'Choose a period\u2026',
  'shape.copiedFrom': 'These dates were copied from {label}. Editing the library will not move them.',
  'shape.takesHolidayWindow': 'A rate covering single nights takes the holiday itself.',
  'shape.takesAppliesWindow': 'A rate covering several nights takes the wider window the park allows such an offer to be placed in, so a long stay with a few nights either side of the holiday still qualifies.',
  'shape.allowedIn': 'Allowed periods',
  'shape.allowedAnywhere': 'Anywhere inside the dates above.',
  'shape.allowPeriod': 'Also allow it in',
  'shape.excludePeriod': 'Keep it out of',
  'shape.removeWindow': 'Remove the window starting {from}',
  'shape.wholeStayHint': 'A stay has to fit wholly inside one of these windows. Keeping a week rate out of a holiday stops it covering any stay that spans those nights; the guest is priced on whatever is next cheapest.',
  'shape.noPeriods': 'No periods are defined for this season yet. Add them on the Periods tab and they can be filled in here.',
  'shape.periodOutside': 'That period falls outside this rate\u2019s dates.',
  'shape.allowOutside': 'That period lies outside the dates above, so allowing it would change nothing. Widen the dates first.',
  'shape.excludeEverything': 'That would leave nowhere for this rate to be used. Change its dates instead, or delete it.',

  'shape.legendDays': 'Which days',
  'shape.arriveOn': 'This block may start on',
  'shape.arriveOnHint': 'On a one-night rate this means: these are the nights it prices.',
  'shape.departOn': 'This block may end on',
  'shape.departOnHint': 'Rarely needed on its own; set it when the block must finish on a fixed day.',
  'shape.stayArriveOn': 'The whole stay must arrive on',
  'shape.stayArriveOnHint': 'A changeover day. A stay arriving on another day cannot use this offer at all.',

  'shape.legendExtend': 'Longer stays',
  'shape.extends': 'Guests may keep extending this offer',
  'shape.extendsHint': 'A fortnight is then two of these, a third week a third — without writing a rate for each length.',
  'shape.stepNights': 'Each step adds (nights)',
  'shape.maxSteps': 'At most (steps)',
  'shape.maxStepsHint': 'Leave empty for no limit.',
  'shape.noLimit': 'no limit',
  'shape.resumeAfterGap': 'Carry on where it left off after an interruption',
  'shape.resumeAfterGapHint': 'When a package interrupts a long stay, the steps continue at the price they had reached instead of starting again.',

  'shape.taperLegend': 'Do the later nights get cheaper?',
  'shape.taperMode.none': 'No — every step costs the same',
  'shape.taperMode.perNight': 'Each night after a point costs a percentage of the night before',
  'shape.taperMode.wholeStay': 'The whole run gets cheaper the longer the stay',
  'shape.taperAfter': 'Starting after (steps)',
  'shape.taperPercent': 'Percentage (%)',
  'shape.taperPercentHint': '95 means each further step costs 95% of the one before, compounding. Decimals matter here: over fifty steps, 99 and 98 are tens of euros apart.',

  'shape.legendWhoMayUse': 'Which stays may use it',
  'shape.stayMin': 'Stay of at least (nights)',
  'shape.stayMax': 'Stay of at most (nights)',
  'shape.maxPlacements': 'At most (times) in one booking',
  'shape.maxPlacementsHint': 'Use for an offer a guest may only take once, such as an arrival discount.',

  'shape.legendEffect': 'What it does to a booking',
  'shape.availability.open': 'Bookable at this price',
  'shape.availability.refuses': 'Priced, but the booking cannot be taken',
  'shape.availability.closed': 'Blocks these nights entirely',
  'shape.needsStaff': 'Reception must confirm before it is booked',
  'shape.needsStaffHint': 'An offer can be available and still be arranged by hand.',
  'shape.message': 'Note shown with the price',

  'programme.shapeHeading': 'What this offer is',
  'programme.find': 'Find a rule',
  'programme.findPlaceholder': 'name, id, length or date',
  'programme.findNothing': 'No rule matches \u201c{find}\u201d.',
  'programme.addRule': 'Add a rule:',
  'programme.name': 'Name',
  'programme.shapeNote': 'Read the sentence back before you save: a wrong window does not show up as a wrong number.',

  'matrix.filterPeriods': 'Find a period',
  'matrix.filterShowing': 'Showing {shown} of {total} periods',
  'matrix.filterClear': 'show all',
  'matrix.filterNoMatch': 'No period matches \u201c{query}\u201d. The periods themselves live on the Periods tab.',

  'matrix.openNightsTitle': 'Open the night calendar for {name} — price single nights, or anything outside a period',
  'matrix.oneNightHint': 'A column here is every night in that period. To make one night different — a dearer Saturday, a night outside every period — open an accommodation’s name above for its night calendar.',

  'calendar.wholeBlock': 'a single night is not sold here — this is the price of a {nights}-night stay',
  'calendar.blockTag': '{nights}n',
  'calendar.excludesExtras': 'Accommodation only. Tourist tax, cleaning and other extras are added on top and are set under Guests & extras.',

  'shape.solverLegend': 'Or say where it should end up, and let it work out the rate',
  'shape.solverTarget': 'Down to (per step)',
  'shape.solverByStep': 'By night number',
  'shape.solverApply': 'Work out the rate',
  'shape.solverHint': 'A step is {nights} night(s) and starts at {price}. Say what it should have come down to and by which night, and the percentage above is worked out for you. It stays editable, and the floor above is left alone.',
  'shape.solverNoStepPrice': 'Give the extension step a price first — there is nothing for the taper to start from.',

  'ladder.staleNote': 'The card has changed since these rungs were read off it. Nothing has been overwritten, but a preview now compares against prices that have moved.',
  'ladder.reread': 'Read the rungs again',

  'shape.solverNotDownwards': 'A taper only goes down. Give a price below {price}, which is what a step costs before it starts falling.',
  'shape.solverTooEarly': 'Nothing has tapered yet by then. The first night with a reduced price is night {night} — everything before it is at full price by definition.',

  'ladder.basisPremium': 'These rungs are a starting point, not this card’s. Its multi-night rates cost more than the same nights bought singly, so there was no ladder to read back. Change them and press Preview to see what would be written.',
  'ladder.basisNothing': 'These rungs are a starting point, not this card’s. It has no multi-night rates to read a ladder from. Change them and press Preview to see what would be written.',

};

export type Catalogue = typeof en;
export type MessageKey = keyof Catalogue;
